import { fazerLogout } from '../utils/logout';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import { useNavigate } from 'react-router-dom';
import Logo from './Logo';
import '../styles/Dashboard.css';
import { getAuthSession } from '../utils/authStorage';

Chart.register(...registerables);

function Dashboard() {
  const [cliente,    setCliente]    = useState(null);
  const [pedidos,    setPedidos]    = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [statusResumo, setStatusResumo] = useState({});
  const [carregandoResumo, setCarregandoResumo] = useState(false);
  const navigate = useNavigate();

  const extractSaleType = (p) => {
    if (!p) return null;
    const candidates = [
      p.saleType,
      p.sale_type,
      p.saleTypeId,
      p.saleTypeID,
      p.saleTypeCode,
      p.tipoVenda,
      p.type,
      p.orderType,
      p.sale_type_code,
    ];
    for (const c of candidates) {
      if (c !== undefined && c !== null) return String(c).trim();
    }
    return null;
  };

  const isRelevantOrder = (pedido) => {
    const tipo = extractSaleType(pedido);
    return tipo === '7';
  };

  const getPedidoKey = (pedido) => String(pedido.orderId ?? pedido.idPedido ?? pedido.id ?? '').trim();

  const dashboardPedidosCanvasRef = useRef(null);
  const dashboardGastoCanvasRef = useRef(null);
  const pedidosChartInstanceRef = useRef(null);
  const gastoChartInstanceRef = useRef(null);

  const getLastMonths = (count = 3) => {
    const now = new Date();
    return Array.from({ length: count }, (_, idx) => {
      const date = new Date(now.getFullYear(), now.getMonth() - (count - 1 - idx), 1);
      const label = date.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
      return {
        year: date.getFullYear(),
        month: date.getMonth(),
        label: label.charAt(0).toUpperCase() + label.slice(1),
        key: `${date.getFullYear()}-${date.getMonth()}`,
      };
    });
  };

  const chartMonths = useMemo(() => getLastMonths(3), []);

  const pedidosPorMes = useMemo(() => {
    const summary = chartMonths.map(() => ({ count: 0, total: 0 }));
    pedidos.filter(isRelevantOrder).forEach((pedido) => {
      const dataPedido = pedido.createData || pedido.orderDate || pedido.createDate || pedido.date;
      const date = dataPedido ? new Date(dataPedido) : null;
      if (!date || Number.isNaN(date.getTime())) return;
      const key = `${date.getFullYear()}-${date.getMonth()}`;
      const index = chartMonths.findIndex((m) => m.key === key);
      if (index === -1) return;
      const valor = Number(pedido.totalValue ?? pedido.valorTotal ?? 0) || 0;
      summary[index].count += 1;
      summary[index].total += valor;
    });
    return summary;
  }, [pedidos, chartMonths]);

  const chartLabels = chartMonths.map((item) => item.label);
  const chartOrderCounts = pedidosPorMes.map((item) => item.count);
  const chartTotalValues = pedidosPorMes.map((item) => Number(item.total.toFixed(2)));
  const latestTotal = pedidosPorMes[2]?.total ?? 0;
  const previousTotal = pedidosPorMes[1]?.total ?? 0;
  const totalVariationPercent = previousTotal > 0
    ? Math.round(((latestTotal - previousTotal) / previousTotal) * 100)
    : null;
  const totalVariationText = totalVariationPercent !== null
    ? `${totalVariationPercent >= 0 ? '+' : ''}${totalVariationPercent}% vs ${chartMonths[1].label}`
    : latestTotal > 0 ? `Novo mês` : 'Sem dados';
  const totalVariationClass = totalVariationPercent !== null && totalVariationPercent < 0
    ? 'grafico-card__variacao--negativa'
    : 'grafico-card__variacao';

  useEffect(() => {
    if (!dashboardPedidosCanvasRef.current || !dashboardGastoCanvasRef.current) return;

    const pedidosCtx = dashboardPedidosCanvasRef.current.getContext('2d');
    const gastoCtx = dashboardGastoCanvasRef.current.getContext('2d');

    pedidosChartInstanceRef.current?.destroy();
    gastoChartInstanceRef.current?.destroy();

    pedidosChartInstanceRef.current = new Chart(pedidosCtx, {
      type: 'bar',
      data: {
        labels: chartLabels,
        datasets: [{
          label: 'Pedidos',
          data: chartOrderCounts,
          backgroundColor: '#004d40',
          borderRadius: 8,
          maxBarThickness: 40,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { precision: 0 },
            grid: { color: 'rgba(0, 0, 0, 0.08)' },
          },
          x: { grid: { display: false } },
        },
      },
    });

    gastoChartInstanceRef.current = new Chart(gastoCtx, {
      type: 'line',
      data: {
        labels: chartLabels,
        datasets: [{
          label: 'Total gasto',
          data: chartTotalValues,
          borderColor: '#004d40',
          backgroundColor: 'rgba(0, 77, 64, 0.12)',
          fill: true,
          tension: 0.3,
          borderWidth: 2,
          pointRadius: 4,
          pointBackgroundColor: '#004d40',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => 'R$ ' + ctx.parsed.y.toLocaleString('pt-BR'),
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { color: 'rgba(0, 0, 0, 0.08)' },
            ticks: {
              callback: (value) => 'R$ ' + (value / 1000) + 'k',
            },
          },
          x: { grid: { display: false } },
        },
      },
    });

    return () => {
      pedidosChartInstanceRef.current?.destroy();
      gastoChartInstanceRef.current?.destroy();
    };
  }, [chartLabels, chartOrderCounts, chartTotalValues]);

  const getSummaryStatus = (detail) => {
    if (!detail || !detail.desdobramento || !Array.isArray(detail.desdobramento.resumoPorProduto)) {
      return null;
    }

    const allComplete = detail.desdobramento.resumoPorProduto.every((item) => {
      const restante = Number(item.qtdRestante ?? 0);
      const pedida = Number(item.qtdPedida ?? 0);
      const desdobrada = Number(item.qtdDesdobrada ?? 0);
      const percentual = pedida > 0 ? Math.min(100, Math.round((desdobrada / pedida) * 100)) : 0;
      return restante <= 0 || percentual >= 100;
    });

    return allComplete ? 'CONCLUÍDO' : 'PENDENTE';
  };

  const getEffectiveStatus = (pedido) => {
    const pedidoKey = getPedidoKey(pedido);
    const resumoEntrada = statusResumo[pedidoKey];
    if (resumoEntrada) return resumoEntrada;
    return null;
  };

  useEffect(() => {
    const { token, customerId, customerName, customerCnpj } = getAuthSession();

    if (!customerId || !token) { navigate('/'); return; }

    setCliente({ id: customerId, nome: customerName, cnpj: customerCnpj });

    const commonHeaders = { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token };

    const ordersPromise = fetch('/api/orders/by-cnpj', {
      method:  'POST',
      headers:  commonHeaders,
    })
      .then(r => r.json())
      .then(data => {
        if (data.ok && data.orders) {
          const filtered = data.orders.filter(isRelevantOrder);
          setPedidos(filtered);
        }
      })
      .catch(() => {});

    const customerPromise = !customerCnpj
      ? fetch('/api/customer/' + encodeURIComponent(customerId) + '?withDeliveryAddress=false', {
          method: 'GET',
          headers: commonHeaders,
        })
          .then(r => r.json())
          .then(data => {
            if (data && data.personIdentificationNumber) {
              setCliente(prev => ({ ...prev, cnpj: data.personIdentificationNumber }));
            }
          })
          .catch(() => {})
      : Promise.resolve();

    Promise.all([ordersPromise, customerPromise])
      .finally(() => setCarregando(false));
  }, [navigate]);

  useEffect(() => {
    const { token } = getAuthSession();
    if (!token || pedidos.length === 0) return;

    const carregarStatusResumo = async () => {
      setCarregandoResumo(true);
      
      // Tenta carregar do cache primeiro
      const cachedResumo = sessionStorage.getItem('statusResumo');
      let initialMap = {};
      
      if (cachedResumo) {
        try {
          initialMap = JSON.parse(cachedResumo);
          setStatusResumo(initialMap);
        } catch {
          // Ignora cache inválido
        }
      }

      // Requisita apenas os que não estão no cache
      const pedidosParaCarregar = pedidos.filter(p => !initialMap[getPedidoKey(p)]);

      if (pedidosParaCarregar.length === 0) {
        setCarregandoResumo(false);
        return;
      }

      // Inicializa com null os que vão ser carregados
      pedidosParaCarregar.forEach((pedido) => {
        initialMap[getPedidoKey(pedido)] = null;
      });
      setStatusResumo(initialMap);

      await Promise.all(pedidosParaCarregar.map(async (pedido) => {
        const pedidoKey = getPedidoKey(pedido);
        try {
          const resp = await fetch(`/api/orders/${encodeURIComponent(pedido.orderId)}/desdobramento`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': 'Bearer ' + token,
            },
          });
          const json = await resp.json();
          if (json.ok && Array.isArray(json.resumoPorProduto)) {
            const summary = getSummaryStatus({ desdobramento: json });
            setStatusResumo((prev) => {
              const updated = {
                ...prev,
                [pedidoKey]: summary,
              };
              // Salva o cache atualizado
              sessionStorage.setItem('statusResumo', JSON.stringify(updated));
              return updated;
            });
          }
        } catch {
          // Ignora erros silenciosamente
        }
      }));
      setCarregandoResumo(false);
    };

    carregarStatusResumo();
  }, [pedidos]);

  const pedidosFiltrados = useMemo(() => pedidos, [pedidos]);
  const pedidosMontados   = useMemo(() => {
    const count = pedidosFiltrados.filter(p => {
      const status = getEffectiveStatus(p);
      return status === 'PENDENTE';
    }).length;
    const temResumo = Object.values(statusResumo).some(v => v !== null);
    return { count, carregando: carregandoResumo && !temResumo };
  }, [pedidosFiltrados, statusResumo, carregandoResumo]);
  
  const pedidosEntregues  = useMemo(() => {
    const count = pedidosFiltrados.filter(p => {
      const status = getEffectiveStatus(p);
      return status === 'CONCLUÍDO';
    }).length;
    const temResumo = Object.values(statusResumo).some(v => v !== null);
    return { count, carregando: carregandoResumo && !temResumo };
  }, [pedidosFiltrados, statusResumo, carregandoResumo]);
  
  const pedidosCancelados = useMemo(() => {
    const count = pedidosFiltrados.filter(p => p.orderStatus === 'C').length;
    const temResumo = Object.values(statusResumo).some(v => v !== null);
    return { count, carregando: carregandoResumo && !temResumo };
  }, [pedidosFiltrados, statusResumo, carregandoResumo]);

  const handleLogout = () => fazerLogout(navigate);

  const handleStatCardClick = (type) => {
    switch (type) {
      case 'total':     navigate('/pedidos?filter=todos'); break;
      case 'montados':  navigate('/pedidos?filter=M');    break;
      case 'entregues': navigate('/pedidos?filter=F');    break;
      case 'cancelados':navigate('/pedidos?filter=C');    break;
      case 'cnpj':      navigate('/perfil');              break;
      default: break;
    }
  };

  // Formata CNPJ para exibição
  const formatarCnpj = (doc) => {
    if (!doc) return 'N/A';
    const s = String(doc).replace(/\D/g, '');
    if (s.length === 14) return s.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    if (s.length === 11) return s.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    return doc;
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-left">
          <Logo size="small" onClick={() => navigate('/dashboard')} />
          <div className="header-text">
            <h1>Dashboard</h1>
            <p className="cliente-info">{cliente ? cliente.nome : 'Carregando...'}</p>
          </div>
        </div>
        <nav className="nav-buttons">
          <button onClick={() => navigate('/pedidos')}  className="btn-nav">Pedidos</button>
          <button onClick={() => navigate('/consulta')} className="btn-nav">Consultar</button>
          <button onClick={handleLogout} className="btn-logout">
            <span className="btn-logout__icon" aria-hidden>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M16 17l5-5-5-5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M21 12H9" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 19H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            Sair
          </button>
        </nav>
      </header>

      <div className="dashboard-content">
        {carregando ? (
          <div className="loading" style={{ textAlign: 'center', padding: 60, color: '#666' }}>
            Carregando seus dados...
          </div>
        ) : (
          <>
            <div className="metricas">
              <div className="metrica-card" onClick={() => handleStatCardClick('total')}>
                <p className="metrica-card__label metrica-card__label--total">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="5" width="18" height="14" rx="2" /><path d="M7 9h10M7 13h10" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Total de pedidos
                </p>
                <p className="metrica-card__valor">{pedidosFiltrados.length}</p>
              </div>

              <div className="metrica-card" onClick={() => handleStatCardClick('montados')}>
                <p className="metrica-card__label metrica-card__label--montados">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="9" /><polyline points="12 6 12 12 16 14" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Pedidos pendentes
                </p>
                <p className="metrica-card__valor">{pedidosMontados.carregando ? <span className="skeleton-loader"></span> : pedidosMontados.count}</p>
              </div>

              <div className="metrica-card" onClick={() => handleStatCardClick('entregues')}>
                <p className="metrica-card__label metrica-card__label--entregues">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="9" /><polyline points="9 12 11 14 15 10" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Pedidos concluídos
                </p>
                <p className="metrica-card__valor">{pedidosEntregues.carregando ? <span className="skeleton-loader"></span> : pedidosEntregues.count}</p>
              </div>

              <div className="metrica-card" onClick={() => handleStatCardClick('cancelados')}>
                <p className="metrica-card__label metrica-card__label--cancelados">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="9" /><line x1="9" y1="9" x2="15" y2="15" /><line x1="15" y1="9" x2="9" y2="15" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Pedidos cancelados
                </p>
                <p className="metrica-card__valor">{pedidosCancelados.carregando ? <span className="skeleton-loader"></span> : pedidosCancelados.count}</p>
              </div>
            </div>

            <div className="acoes-rapidas">
              <p className="acoes-rapidas__titulo">Ações rápidas</p>

              <div className="acoes-rapidas__grid">
                <button onClick={() => navigate('/perfil')} className="acoes-rapidas__botao">
                  <span className="acoes-rapidas__icone" aria-hidden>
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6">
                      <path d="M12 12c2.761 0 5-2.239 5-5s-2.239-5-5-5-5 2.239-5 5 2.239 5 5 5z" />
                      <path d="M4 20c0-3.313 2.687-6 6-6h4c3.313 0 6 2.687 6 6" />
                    </svg>
                  </span>
                  <span className="acoes-rapidas__label">Meu perfil</span>
                </button>

                <button onClick={() => navigate('/pedidos')} className="acoes-rapidas__botao">
                  <span className="acoes-rapidas__icone" aria-hidden>
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6">
                      <rect x="3" y="5" width="18" height="14" rx="2" />
                      <path d="M7 9h10M7 13h10" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span className="acoes-rapidas__label">Ver meus pedidos</span>
                </button>

                <button onClick={() => navigate('/consulta')} className="acoes-rapidas__botao">
                  <span className="acoes-rapidas__icone" aria-hidden>
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6">
                      <circle cx="11" cy="11" r="6" />
                      <path d="M21 21l-4.35-4.35" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span className="acoes-rapidas__label">Consultar por número</span>
                </button>

                <a href="https://linktr.ee/athacado68" target="_blank" rel="noopener noreferrer" className="acoes-rapidas__botao" title="Fale Conosco">
                  <span className="acoes-rapidas__icone" aria-hidden>
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6">
                      <path d="M21 8v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8" />
                      <polyline points="3 8 12 13 21 8" />
                    </svg>
                  </span>
                  <span className="acoes-rapidas__label">Fale Conosco</span>
                </a>
              </div>
            </div>

            <div className="graficos-dashboard">
              <div className="grafico-card">
                <div className="grafico-card__header">
                  <p className="grafico-card__titulo">Pedidos por mês</p>
                  <span className="grafico-card__periodo">3 meses</span>
                </div>
                <div className="grafico-card__canvas-wrap">
                  <canvas ref={dashboardPedidosCanvasRef} />
                </div>
              </div>

              <div className="grafico-card">
                <div className="grafico-card__header">
                  <p className="grafico-card__titulo">Total gasto por mês</p>
                  <span className="grafico-card__periodo">3 meses</span>
                </div>
                <p className="grafico-card__valor">
                  R$ {latestTotal.toLocaleString('pt-BR')}
                  <span className={totalVariationClass}>{totalVariationText}</span>
                </p>
                <div className="grafico-card__canvas-wrap grafico-card__canvas-wrap--compacto">
                  <canvas ref={dashboardGastoCanvasRef} />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
